// packages/web/src/lib/catalog/branding.ts
// Catalog branding logic

import { getPlatformUrl } from '../url-utils';
import { detectCatalogInstance } from './detector';
import type { CatalogBranding } from './types';

/**
 * Get catalog branding information
 * Branding is determined purely by the catalog instance being viewed,
 * not by user preferences. Only partner admins can change their catalog's branding.
 */
export async function getCatalogBranding(): Promise<CatalogBranding> {
  console.log('[CATALOG-BRANDING] ===== getCatalogBranding START =====');
  console.log('[CATALOG-BRANDING] Determining branding based on current catalog instance');

  // Always detect catalog from URL first - branding follows the catalog being viewed
  const catalog = await detectCatalogInstance();
  const catalogInstance = (catalog as any)?.catalogInstance || catalog;
  console.log('[CATALOG-BRANDING] Detected catalog instance:', {
    id: catalogInstance?.id,
    name: catalogInstance?.name,
    isMainCatalog: catalogInstance?.isMainCatalog,
    hasBranding: !!catalogInstance?.branding,
  });

  // Helper function to get default website URL from environment
  const getDefaultWebsite = (): string => {
    // Use NEXT_PUBLIC_BRAND_WEBSITE if set
    if (process.env.NEXT_PUBLIC_BRAND_WEBSITE) {
      return process.env.NEXT_PUBLIC_BRAND_WEBSITE;
    }
    // Use NEXT_PUBLIC_API_URL origin if set
    if (process.env.NEXT_PUBLIC_API_URL) {
      try {
        const url = new URL(process.env.NEXT_PUBLIC_API_URL);
        return url.origin;
      } catch {
        // If URL parsing fails, fall through to platform URL
      }
    }
    // Use centralized platform URL detection
    return getPlatformUrl();
  };

  // Use branding data from catalog instance if available
  if (catalogInstance.branding) {
    console.log('[CATALOG-BRANDING] Using catalog instance branding');
    return {
      logo: catalogInstance.branding.logo || '/assets/brands/default/logo.webp',
      primaryColor: catalogInstance.branding.primaryColor || '#D4AF37',
      secondaryColor: catalogInstance.branding.secondaryColor || '#dc004e',
      name: catalogInstance.branding.name || catalogInstance.name,
      website: catalogInstance.branding.website,
      ...catalogInstance.branding, // Include any additional branding properties
    };
  }

  // Fallback branding based on catalog type
  if (catalogInstance.isMainCatalog) {
    console.log(
      '[CATALOG-BRANDING] Main catalog detected, checking user role for platform branding access',
    );

    // Check if user is a super-admin before trying to fetch platform branding
    try {
      const { hasAuthTokens } = await import('../auth/session-manager');
      const isAuthenticated = hasAuthTokens();

      if (isAuthenticated) {
        // Check user role from Redux store first, then localStorage
        let userRole = 'customer'; // default
        try {
          if (typeof window !== 'undefined') {
            // Try Redux store first (more current)
            try {
              const { store } = await import('../../store');
              const reduxUser = (store.getState() as any)?.auth?.user;
              if (reduxUser?.role) {
                userRole = reduxUser.role;
                console.log('[CATALOG-BRANDING] Using user role from Redux:', userRole);
              } else {
                // Fall back to localStorage
                const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
                userRole = storedUser?.role || 'customer';
                console.log('[CATALOG-BRANDING] Using user role from localStorage:', userRole);
              }
            } catch (reduxError) {
              // Redux not available, use localStorage
              const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
              userRole = storedUser?.role || 'customer';
              console.log(
                '[CATALOG-BRANDING] Redux not available, using localStorage role:',
                userRole,
              );
            }
          }
        } catch (e) {
          console.warn('[CATALOG-BRANDING] Could not determine user role:', e);
        }

        // Only super-admins can fetch platform branding
        if (userRole === 'super_admin') {
          console.log('[CATALOG-BRANDING] Super-admin user detected, fetching platform branding');
          try {
            const { apiFetch } = await import('../api');
            const platformBranding = await apiFetch('/api/super-admin/platform/branding', {
              method: 'GET',
            });
            console.log('[CATALOG-BRANDING] Platform branding fetched successfully');
            return {
              logo: platformBranding.logo || '/assets/brands/default/logo.webp',
              primaryColor: platformBranding.primaryColor || '#D4AF37',
              secondaryColor: platformBranding.secondaryColor || '#dc004e',
              name: platformBranding.name || 'Aithon',
              website: platformBranding.website || getDefaultWebsite(),
            };
          } catch (error: any) {
            console.warn(
              '[CATALOG-BRANDING] Failed to fetch platform branding (user may have switched roles), falling back to defaults:',
              error,
            );
            // If API call fails (e.g., 403 Forbidden), fall back to defaults
            // This handles the case where user switches from super-admin to customer
          }
        } else {
          console.log('[CATALOG-BRANDING] Non-super-admin user, using default Aithon branding');
        }
      } else {
        console.log('[CATALOG-BRANDING] User not authenticated, using default Aithon branding');
      }
    } catch (error) {
      console.warn(
        '[CATALOG-BRANDING] Failed to fetch platform branding (expected for non-super-admin users), using defaults:',
        error,
      );
    }

    // Always fall back to default Aithon branding for non-super-admin users
    return {
      logo: '/assets/brands/default/logo.webp',
      primaryColor: '#D4AF37',
      secondaryColor: null,
      name: 'Aithon',
      website: getDefaultWebsite(),
    };
  }

  // Partner catalog - use branding data from catalog instance
  console.log(
    '[CATALOG-BRANDING] Partner catalog detected, using branding from catalog instance',
    catalogInstance.branding || 'no branding data',
  );

  // Use branding from catalog instance if available, otherwise use dynamic logo system
  if (catalogInstance.branding) {
    console.log('[CATALOG-BRANDING] Using catalog instance branding:', catalogInstance.branding);
    return {
      logo:
        catalogInstance.branding.logo ||
        catalogInstance.branding.logoUrl ||
        '/assets/brands/default/logo.webp',
      primaryColor: catalogInstance.branding.primaryColor || '#D4AF37',
      secondaryColor: catalogInstance.branding.secondaryColor || '#1976d2',
      name: catalogInstance.branding.name || catalogInstance.name,
      website: catalogInstance.branding.website,
    };
  }

  // Fallback: use dynamic logo from brands table via API
  console.log('[CATALOG-BRANDING] No branding data on catalog instance, fetching from API');

  // Try to get branding from the catalog API endpoint
  try {
    const subdomain =
      catalogInstance.subdomain ||
      catalogInstance.catalogName?.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (subdomain) {
      const response = await fetch(`/api/marketplace/c/${subdomain}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.branding?.logo) {
          console.log('[CATALOG-BRANDING] Found logo from API:', data.branding.logo);
          return {
            logo: data.branding.logo,
            primaryColor: data.branding.primaryColor || '#D4AF37',
            secondaryColor: data.branding.secondaryColor || '#1976d2',
            name: data.branding.name || catalogInstance.name,
            website: data.branding.website,
          };
        }
      }
    }
  } catch (error) {
    console.warn('[CATALOG-BRANDING] Failed to fetch branding from API:', error);
  }

  // Final fallback - use dynamic logo from tenantId
  console.log('[CATALOG-BRANDING] Using fallback branding');
  return {
    logo: catalogInstance.tenantId
      ? `/api/logos/${catalogInstance.tenantId}`
      : '/assets/brands/default/logo.webp',
    primaryColor: '#D4AF37',
    secondaryColor: '#1976d2',
    name: catalogInstance.name,
    website: undefined,
  };
}
